import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import useGeoBlock from './hooks/useGeoBlock';
import Home from './pages/Home';
import JobListings from './pages/JobListings';
import JobDetails from './pages/JobDetails';
import ApplicationForm from './pages/ApplicationForm';
import ContactUs from './pages/ContactUs';
import ApplicationSuccess from './pages/ApplicationSuccess';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import AboutUs from './pages/AboutUs';
import WhyCapstone from './pages/WhyCapstone';
import QuickApply from './pages/QuickApply';
import NotFound from './pages/NotFound';
import RegionUnavailable from './pages/RegionUnavailable';
import Redirects from './components/Redirects';
import ThankYou from './pages/ThankYou';
import ReferenceRedirect from './components/ReferenceRedirect';
import CandidateReferenceRedirect from './components/CandidateReferenceRedirect';
import OnboardingUploadRedirect from './components/OnboardingUploadRedirect';
import Nursing from './pages/Nursing';
import LongTermCare from './pages/LongTermCare';
import CathLabs from './pages/CathLabs';
import TravelNursing from './pages/TravelNursing';
import Cardiopulmonary from './pages/Cardiopulmonary';
import Imaging from './pages/Imaging';
import Laboratory from './pages/Laboratory';
import Therapy from './pages/Therapy';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
// DEPRECATED: Recruiter Riley page no longer in use (Feb 2026)
// import RecruiterRiley from './pages/RecruiterRiley';
import JobMap from './pages/JobMap';
import JobRedirect from './components/JobRedirect';
import ExternalRedirect from './components/ExternalRedirect';
import { RecaptchaProvider } from './contexts/RecaptchaContext';
import './styles/global.css';

const Layout = ({ children }) => {
  return (
    <>
      {children}
      <Footer />
    </>
  );
};

const ConditionalNavigation = () => {
  const location = useLocation();
  const isEmbed = location.pathname === '/job-map-embed';

  return !isEmbed ? <Navigation /> : null;
};

const GeoBlockGuard = ({ children }) => {
  const location = useLocation();
  const { isBlocked, isLoading } = useGeoBlock();

  // Don't block the region-unavailable page itself
  if (location.pathname === '/region-unavailable') {
    return children;
  }

  // Show nothing while checking (brief flash)
  if (isLoading) {
    return null;
  }

  // Redirect blocked visitors
  if (isBlocked) {
    return <Navigate to="/region-unavailable" replace />;
  }

  return children;
};

function App() {
  return (
    <RecaptchaProvider>
      <Router>
        <GeoBlockGuard>
          <ConditionalNavigation />
          <Redirects />
          <Routes>
            <Route path="/region-unavailable" element={<RegionUnavailable />} />
        <Route
          path="/"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route
          path="/about"
          element={
            <Layout>
              <AboutUs />
            </Layout>
          }
        />
        <Route
          path="/why-capstone"
          element={
            <Layout>
              <WhyCapstone />
            </Layout>
          }
        />
        <Route
          path="/jobs"
          element={
            <Layout>
              <JobListings />
            </Layout>
          }
        />
        <Route
          path="/jobs/:jobId"
          element={
            <Layout>
              <JobDetails />
            </Layout>
          }
        />
        <Route
          path="/apply/:jobId"
          element={
            <Layout>
              <ApplicationForm />
            </Layout>
          }
        />
        <Route
          path="/contact"
          element={
            <Layout>
              <ContactUs />
            </Layout>
          }
        />
        <Route
          path="/application-success"
          element={
            <Layout>
              <ApplicationSuccess />
            </Layout>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <Layout>
              <PrivacyPolicy />
            </Layout>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <Layout>
              <TermsAndConditions />
            </Layout>
          }
        />

        <Route
          path="/Nursing-Staffing-Services"
          element={
            <Layout>
              <Nursing />
            </Layout>
          }
        />
        <Route
          path="/Long-Term-Care-Nurse-Jobs"
          element={
            <Layout>
              <LongTermCare />
            </Layout>
          }
        />
        <Route
          path="/Cath-Labs-Technologist-Jobs"
          element={
            <Layout>
              <CathLabs />
            </Layout>
          }
        />
        <Route
          path="/Travel-Nursing-Jobs"
          element={
            <Layout>
              <TravelNursing />
            </Layout>
          }
        />
        <Route
          path="/Cardiopulmonary-Jobs"
          element={
            <Layout>
              <Cardiopulmonary />
            </Layout>
          }
        />
        <Route
          path="/Imaging-Jobs"
          element={
            <Layout>
              <Imaging />
            </Layout>
          }
        />
        <Route
          path="/Laboratory-Technologist-Jobs"
          element={
            <Layout>
              <Laboratory />
            </Layout>
          }
        />
        <Route
          path="/Therapy-Social-Worker-Jobs"
          element={
            <Layout>
              <Therapy />
            </Layout>
          }
        />
                <Route
          path="/quickapply"
          element={
            <Layout>
              <QuickApply />
            </Layout>
          }
        />
        <Route
          path="/job-map"
          element={
            <Layout>
              <JobMap />
            </Layout>
          }
        />
        <Route
          path="/job-map-embed"
          element={<JobMap embed={true} />}
        />
{/* DEPRECATED: Recruiter Riley page no longer in use (Feb 2026)
        <Route
          path="/recruiter-riley"
          element={
            <Layout>
              <RecruiterRiley />
            </Layout>
          }
        />
*/}
        <Route
          path="/thank-you"
          element={
            <Layout>
              <ThankYou />
            </Layout>
          }
        />
        <Route
          path="/reference"
          element={<ReferenceRedirect key={window.location.search} />}
        />
        <Route
          path="/ref"
          element={<CandidateReferenceRedirect />}
        />
        <Route
          path="/upload"
          element={<OnboardingUploadRedirect />}
        />
        <Route
          path="/onboarding"
          element={<OnboardingUploadRedirect />}
        />

        {/* Job Search Short URLs for SMS Campaigns */}
        <Route
          path="/medtech"
          element={<JobRedirect url="/jobs?title=Med+Tech%2CMedical+Laboratory+Tech" />}
        />
        <Route
          path="/nuclearmedicine"
          element={<JobRedirect url="/jobs?specialty=Nuclear+Medicine" />}
        />
        <Route
          path="/ot"
          element={<JobRedirect url="/jobs?title=OT" />}
        />
        <Route
          path="/pta"
          element={<JobRedirect url="/jobs?specialty=PTA" />}
        />
        <Route
          path="/pt"
          element={<JobRedirect url="/jobs?title=PT" />}
        />
        <Route
          path="/surgicaltech"
          element={<JobRedirect url="/jobs?title=Surgical+Tech" />}
        />
        <Route
          path="/resptherapy"
          element={<JobRedirect url="/jobs?title=Respiratory+Therapist" />}
        />
        <Route
          path="/radtech"
          element={<JobRedirect url="/jobs?title=Rad+Tech" />}
        />
        <Route
          path="/rn"
          element={<JobRedirect url="/jobs?title=RN" />}
        />
        <Route
          path="/cota"
          element={<JobRedirect url="/jobs?specialty=COTA" />}
        />
        <Route
          path="/ct"
          element={<JobRedirect url="/jobs?specialty=CT" />}
        />
        <Route
          path="/mri"
          element={<JobRedirect url="/jobs?specialty=MRI" />}
        />
        <Route
          path="/mlt"
          element={<JobRedirect url="/jobs?specialty=Medical+Laboratory+Tech+%28MLT%29" />}
        />
        <Route
          path="/slp"
          element={<JobRedirect url="/jobs?title=SLP" />}
        />
        <Route
          path="/sw"
          element={<JobRedirect url="/jobs?title=Social+Worker" />}
        />

        {/* Scheduling Links */}
        <Route
          path="/evan"
          element={<ExternalRedirect url="https://calendar.cirrusinsight.com/schedule/evan-sersland" />}
        />
        <Route
          path="/john"
          element={<ExternalRedirect url="https://calendar.cirrusinsight.com/schedule/john-bannos" />}
        />
        <Route
          path="/steven"
          element={<ExternalRedirect url="https://calendar.cirrusinsight.com/schedule/steven-greger" />}
        />

        <Route
          path="*"
          element={
            <Layout>
              <NotFound />
            </Layout>
          }
        />
          </Routes>
        </GeoBlockGuard>
      </Router>
    </RecaptchaProvider>
  );

}

export default App;
